import { Phone, EnvelopeSimple, MapPin, WhatsappLogo, FacebookLogo, InstagramLogo, YoutubeLogo } from "@phosphor-icons/react";
import { IMG } from "@/lib/products";
import { useLang } from "@/lib/i18n";
import { PageHero } from "@/components/PageBits";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { SITE, openWhatsApp } from "@/lib/site";

const CONTACT_SOCIAL = [
  { key: "instagram", href: SITE.social.instagram, Icon: InstagramLogo, label: "Instagram" },
  { key: "youtube", href: SITE.social.youtube, Icon: YoutubeLogo, label: "YouTube" },
  { key: "facebook", href: SITE.social.facebook, Icon: FacebookLogo, label: "Facebook" },
];

export default function Contact() {
  const { t } = useLang();
  const mapsQuery = encodeURIComponent("Kinetic Chowk, Chayya Nagar, Ahilyanagar, Maharashtra");

  return (
    <div>
      <PageHero
        eyebrow={t("contact.eyebrow")}
        title={t("contact.title")}
        subtitle={t("contact.subtitle")}
        image={IMG.cityWater}
      />

      <section className="container-x py-16 md:py-24" data-testid="contact-page">
        <div className="grid gap-12 lg:grid-cols-3">
          <Reveal className="space-y-5 lg:col-span-1">
            <div className="flex items-start gap-4 rounded-2xl border border-hairline bg-surface p-6 shadow-sm" data-testid="contact-phone">
              <Phone size={26} weight="fill" className="mt-1 shrink-0 text-gold" />
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{t("contact.phone")}</p>
                <p className="mt-2 text-center text-sm font-bold text-forest" data-testid="contact-marketed-by">
                  All India Marketed By New Pritam Agency
                </p>
                <div className="mt-1 flex flex-col gap-1">
                  <a href={`tel:${SITE.phoneRaw}`} className="font-semibold text-forest hover:text-gold transition-colors" data-testid="contact-phone-1">
                    {SITE.phoneDisplay}
                  </a>
                  <a href={`tel:${SITE.phoneRawAlt}`} className="font-semibold text-forest hover:text-gold transition-colors" data-testid="contact-phone-2">
                    {SITE.phoneDisplayAlt}
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-hairline bg-surface p-6 shadow-sm" data-testid="contact-email">
              <EnvelopeSimple size={26} weight="fill" className="mt-1 shrink-0 text-gold" />
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{t("contact.email")}</p>
                <div className="mt-1 flex flex-col gap-1">
                  <a href={`mailto:${SITE.email}`} className="break-all font-semibold text-forest hover:text-gold transition-colors" data-testid="contact-email-1">
                    {SITE.email}
                  </a>
                  <a href={`mailto:${SITE.emailAlt}`} className="break-all font-semibold text-forest hover:text-gold transition-colors" data-testid="contact-email-2">
                    {SITE.emailAlt}
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl border border-hairline bg-surface p-6 shadow-sm" data-testid="contact-address">
              <MapPin size={26} weight="fill" className="mt-1 shrink-0 text-gold" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{t("contact.address")}</p>
                <p className="mt-1 leading-relaxed text-forest">
                  {SITE.addressLines.map((l, i) => <span key={i} className="block">{l}</span>)}
                </p>
              </div>
            </div>
            <button onClick={() => openWhatsApp()} className="btn-gold w-full justify-center" data-testid="contact-whatsapp-button">
              <WhatsappLogo size={20} weight="fill" /> {t("contact.messageWhatsapp")}
            </button>

            <div className="flex items-center gap-3 rounded-2xl border border-hairline bg-surface p-6 shadow-sm" data-testid="contact-socials">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{t("contact.followUs")}</p>
              <div className="ml-auto flex gap-2">
                {CONTACT_SOCIAL.map(({ key, href, Icon, label }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${key}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-champagne text-forest transition-colors hover:bg-forest hover:text-sand"
                  >
                    <Icon size={18} weight="fill" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <EnquiryForm />
          </Reveal>
        </div>
      </section>

      <section className="pb-20" data-testid="contact-map">
        <div className="container-x">
          <div className="overflow-hidden rounded-2xl border border-hairline shadow-sm">
            <iframe
              title="RK Jain Location"
              src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
              width="100%"
              height="440"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
